import { ComponentRef, Injectable, ViewChild, ViewContainerRef } from '@angular/core';
import { AbcCore, CollectionHelper, CompareHelper } from '@barthauer/abc/core';
import {
  ObjectKey,
  Query,
  QueryColumn,
  QueryHelper,
  QueryObjectsModel,
  QueryOutputMode,
  ReadDocumentModel,
  ReadListDataSourceModel,
  ReadPicklistItemsModel,
  TargetColumnValue,
} from '@barthauer/abc/core/data';
import {
  ParameterDefinition,
  ParameterHelper,
  ParameterType,
  ParameterValue,
} from '@barthauer/abc/queries/parameters/core';
import { AbcContainerService, AbcWindowComponent, IWindow, WindowStyle } from '@barthauer/abc/ui-components';
import { Observable, of, Subject } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { ValuesEditorContainerComponent } from '../values-editor-container/values-editor-container.component';

@Injectable({
  providedIn: 'root',
})
export class ParameterService {
  @ViewChild('abcWindowContainer', { read: ViewContainerRef }) abcWindow: ViewContainerRef;
  containerComponentRef: ComponentRef<any>;
  windowInstance: AbcWindowComponent;
  valuesEditorContainerInstance: ValuesEditorContainerComponent;
  public hideOnOutsideClick = false;

  public loadParameterDefinitions: () => Promise<ParameterDefinition[]>;
  public saveParameterDefinitions: (parameterDefinitions: ParameterDefinition[]) => Promise<void>;

  constructor(
    private abcCore: AbcCore,
    private abcContainer: AbcContainerService
  ) {}

  public queryPicklistItems(picklistname: string, language: number) {
    const observable$ = new Observable((observer) => {
      const queryPicklistItems: ReadPicklistItemsModel = new ReadPicklistItemsModel();
      queryPicklistItems.Indent = true;
      queryPicklistItems.OutputMode = 0;
      queryPicklistItems.Name = picklistname;
      this.abcCore.data.executeReadPicklistsItemsQuery(queryPicklistItems).subscribe((result) => {
        observer.next(result);
      });
    });
    return observable$;
  }

  public queryPicklistItemsByObjectTypeAndColumnName(objectType: string, columnName: string) {
    const readListDataSourceModel = new ReadListDataSourceModel();
    const objectKey = new ObjectKey();
    objectKey.ObjectType = objectType;
    readListDataSourceModel.ColumnName = columnName;
    readListDataSourceModel.ObjectKey = objectKey;
    return this.abcCore.data.executeReadListDataSource(readListDataSourceModel);
  }

  public queryObjectDataSourceByObjectType(objectType: string): Observable<any> {
    const readListDataSourceModel = new ReadListDataSourceModel();
    const objectKey = new ObjectKey();
    objectKey.ObjectType = objectType;
    readListDataSourceModel.ColumnName = 'Id';
    readListDataSourceModel.ObjectKey = objectKey;

    return this.abcCore.data.executeReadListDataSource(readListDataSourceModel);
  }

  public queryObjectDataSourceByObjectTypeEx(
    objectType: string | undefined,
    filter: string | undefined
  ): Observable<any> {
    const readListDataSourceModel = new ReadListDataSourceModel();
    const objectKey = new ObjectKey();
    objectKey.ObjectType = objectType || '';
    readListDataSourceModel.ColumnName = 'Id';
    readListDataSourceModel.ObjectKey = objectKey;
    if (filter) readListDataSourceModel.CustomFilterExpression = filter;
    return this.abcCore.data.executeReadListDataSource(readListDataSourceModel);
  }

  public queryTargetColumnValuesByObjectTypeAndColumn(
    objectType: string,
    columnName: string
  ): Observable<TargetColumnValue[]> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = objectType;
    query.IsDistinct = true;
    const colums: QueryColumn[] = [];

    colums.push({ Name: columnName, OPath: columnName });
    query.Columns = colums;
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];
    return this.abcCore.data.executeReadObjectsQuery(queryDocument).pipe(
      concatMap((result) => {
        const values: TargetColumnValue[] = JSON.parse(JSON.stringify(result));
        return of(values);
      })
    );
  }

  private queryParameterDefinitionsFromDocument(documentId: string): Observable<ParameterDefinition[]> {
    const query: ReadDocumentModel = new ReadDocumentModel();
    query.Id = documentId;
    query.Indent = true;
    return this.abcCore.data.executeReadQueryFromDocumentQuery(query).pipe(
      map((results) => {
        let paraDoc: QueryObjectsModel = JSON.parse(JSON.stringify(results));
        return paraDoc?.ParameterDefinitions;
      })
    );
  }

  public queryDataFromQueryDefinitionAsArray(query: QueryObjectsModel): Observable<object[]> {
    return this.abcCore.data
      .executeReadObjectsQuery(query)
      .pipe(map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])));
  }

  public executeDocumentParametersIfExist(
    documentId: string,
    parameterNamesToExclude?: string[]
  ): Observable<ParameterValue[]> {
    return this.queryParameterDefinitionsFromDocument(documentId).pipe(
      concatMap((paras) => {
        return this.fillParametersIfExist(paras, parameterNamesToExclude);
      })
    );
  }

  private excludeParameters(
    parameters: ParameterDefinition[],
    parameterNamesToExclude?: string[],
    query?: QueryObjectsModel
  ): ParameterDefinition[] {
    let result: ParameterDefinition[] = parameters;
    if (query) {
      result = QueryHelper.findParametersInUse(query, result);
    }

    if (parameterNamesToExclude?.length) {
      result = result?.filter((p) => !parameterNamesToExclude?.includes(p.Name));
    }
    return result;
  }

  public tryFindChangeableParameters(
    queryOrParameters: QueryObjectsModel | ParameterDefinition[],
    parameterNamesToExclude?: string[]
  ): ParameterDefinition[] {
    let parameters: ParameterDefinition[];
    if (Array.isArray(queryOrParameters)) {
      parameters = this.excludeParameters(queryOrParameters, parameterNamesToExclude);
    } else {
      parameters = this.excludeParameters(
        queryOrParameters?.ParameterDefinitions,
        parameterNamesToExclude,
        queryOrParameters
      );
    }

    return parameters?.filter((p) => p.IsFixed !== true);
  }

  paraDialogData$: Observable<ParameterValue[]>;
  public async fillParametersIfExist(
    parameters: ParameterDefinition[],
    parameterNamesToExclude?: string[]
  ): Promise<ParameterValue[]> {
    if (this.containerComponentRef) {
      this.containerComponentRef.destroy();
    }

    const parameterDefinitions = this.excludeParameters(parameters, parameterNamesToExclude);
    if (parameterDefinitions?.length) {
      return this.getQueryParameters(await this.showParameterEditor(parameterDefinitions));
    }
    return [];
  }

  valuesEditorContainerComponent: ValuesEditorContainerComponent;
  private showParameterEditor(paraDefinitions: ParameterDefinition[]): Promise<ParameterDefinition[]> {
    const style: WindowStyle = {
      height: '100%',
      minHeight: '320px',
      maxHeight: '700px',
      maxWidth: '600px',
      width: '100%',
      resizable: true,
    };

    var subject = new Subject<ParameterDefinition[]>();

    const windowAttr: IWindow = {
      id: 'window-cache-config',
      label: 'Parameter eingeben', //(await this.resAsync).get('CacheConfig-Title'),
      hideOnOutsideClick: this.hideOnOutsideClick || false,
      maximizeButton: true,
      minimizeButton: true,
      minumizeDialogOption: -64,
      component: ValuesEditorContainerComponent,
      isInMobile: true,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };

    const window = this.abcContainer.createComponent(this.abcWindow, AbcWindowComponent, windowAttr);
    this.containerComponentRef = window.componentRef;
    this.windowInstance = <AbcWindowComponent>window.componentRef.instance;

    this.windowInstance.createComponentInstance().subscribe((windowComponent) => {
      this.valuesEditorContainerInstance = <ValuesEditorContainerComponent>windowComponent.openWindow().instance;
      this.valuesEditorContainerInstance.paraDefinitions = CollectionHelper.orderBy(
        paraDefinitions,
        (_) => _.SortIndex ?? -1
      );
      this.valuesEditorContainerInstance.parameterDialogApplied.subscribe(() => {
        subject.next(this.valuesEditorContainerInstance.paraDefinitions);
        subject.complete();
        this.windowInstance.closeDialog();
      });
      this.valuesEditorContainerInstance.parameterDialogCancelled.subscribe(() => {
        subject.next(undefined);
        subject.complete();
        this.windowInstance.closeDialog();
      });

      windowComponent.onAfterClosed.subscribe(() => {
        this.valuesEditorContainerInstance.parameterDialogCancelled.emit();
        window.componentRef.destroy();
      });
    });

    return subject.asObservable().toPromise();
  }

  private getQueryParameters(parameterDefinitions: ParameterDefinition[]): ParameterValue[] {
    const parameterValues: ParameterValue[] = [];
    if (parameterDefinitions?.length) {
      for (var pa of parameterDefinitions) {
        const val = pa.Type === ParameterType.Date ? ParameterHelper.getDateValue(pa.Value) : pa.Value;
        parameterValues.push(new ParameterValue(pa.Name, val));
      }
    }
    return parameterValues;
  }
}
